
import React, { useState, useEffect } from 'react';
import { Box, Button, Container, TextField, Typography, Stack, CircularProgress, IconButton, Alert, AlertTitle } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import AppAppBar from '../../components/layout/AppAppBar';

import TaskSteps from '../../components/common/TaskSteps';
import { toast } from 'react-toastify';
import { BASE_URL } from '../../config';
import { ApiManager } from '../../utils/apiManager';
import { captureAndDownloadScreenshot, sendScreenshotViaEmail } from '../../utils/screenshotCapture';


export default function TaskSubmissionPage() {
  const [email, setEmail] = useState('');
  const [paypal, setPaypal] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [formName, setFormName] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    // Get form name from localStorage (set when user clicks install)
    const storedFormName = localStorage.getItem('current_form_name');
    if (storedFormName) {
      setFormName(storedFormName);
    } else {
      // Fallback: try to get from URL
      // const currentUrl = window.location.pathname;
      // const parts = currentUrl.split('/').filter((part) => part !== '');

      // Default form name if nothing found
      setFormName('Ring Form');
      console.warn('No form name found in localStorage, using default');
    }

    // Autofill email if logged in
    const storedEmail = localStorage.getItem('email') || localStorage.getItem('phone');
    if (storedEmail) {
      setEmail(storedEmail);
    } else {
      // Try getting from user_data
      const userDataStr = localStorage.getItem('user_data');
      if (userDataStr) {
        try {
          const userData = JSON.parse(userDataStr);
          if (userData.email) setEmail(userData.email);
          else if (userData.phone_number) setEmail(userData.phone_number);
          else if (userData.phone) setEmail(userData.phone);
        } catch (e) {
          console.error("Error parsing user data", e);
        }
      }
    }


  }, []);

  const handleSubmit = async () => {
    // Clear previous errors
    setErrorMessage('');

    // Validation
    if (!email || !paypal || !file) {
      setErrorMessage('Please fill all fields and upload a screenshot');
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setErrorMessage('Please enter a valid email address');
      return;
    }

    if (!formName) {
      setErrorMessage('Form name not found. Please go back and try again.');
      return;
    }

    setIsSubmitting(true);

    try {
      // Create FormData
      const formData = new FormData();
      formData.append('name', formName);
      formData.append('email', email);
      formData.append('image', file);
      formData.append('paypal', paypal);
      formData.append('fest', 'False');

      // Log the data being sent (for debugging)
      console.log('Submitting Form Data:', { formName, email, paypal, fileName: file.name });

      // Submit to API
      const response = await ApiManager.fetch(`${BASE_URL}/api/form/input-ios-data/`, {
        method: 'POST',
        body: formData,
      });

      const responseText = await response.text();
      console.log('API Response:', response.status, responseText);

      if (!response.ok) {
        let errorMsg = 'Submission failed';

        if (response.status === 404) {
          // User friendly message for 404
          errorMsg = "Submission not accepted. You may have already completed this offer or it is no longer active.";
        } else if (response.status >= 500) {
          // User friendly message for Server Errors
          errorMsg = "System is currently busy processing other requests. Please try again in 30 seconds.";
        } else {
          // Try to parse detailed backend message for other codes (400, 401, etc)
          try {
            const errorData = JSON.parse(responseText);
            if (errorData.message) errorMsg = errorData.message;
            else if (errorData.error) errorMsg = errorData.error;
            else if (errorData.detail) errorMsg = errorData.detail;
            else errorMsg = "Verification failed. Please check your inputs.";
          } catch (e) {
            errorMsg = "Unable to verify submission. Please try again.";
          }
        }

        const error: any = new Error(errorMsg);
        error.backendResponse = responseText;
        error.status = response.status;
        throw error;
      }

      // Success
      try {
        const data = JSON.parse(responseText || '{}');
        console.log('Submission Success:', data);
        if (data && data.id) {
          sessionStorage.setItem('submission_id', data.id);
        }


      } catch (e) {
        console.warn('Could not parse success response JSON:', e);
      }

      // Store locally for guest history tracking
      // Store locally for guest history tracking
      const localHistory = JSON.parse(localStorage.getItem('local_offer_history') || '[]');
      const storedAmount = localStorage.getItem('current_offer_amount') || '6.00';

      localHistory.push({
        name: formName,
        email: email,
        date: new Date().toISOString(),
        amount: storedAmount
      });
      localStorage.setItem('local_offer_history', JSON.stringify(localHistory));

      // Ensure email is stored for guest session
      if (!localStorage.getItem('email')) {
        localStorage.setItem('email', email);
      }

      // Set flag to refresh completed offers on dashboard
      localStorage.setItem('refresh_completed_offers', 'true');

      toast.success('Task submitted successfully!');

      // Navigate to correct payment processing page based on source
      setTimeout(() => {
        const adSource = sessionStorage.getItem('ad_source');
        console.log('Ad Source for redirection:', adSource);

        if (adSource && (adSource === 'facebook' || adSource === 'meta' || adSource === 'fb')) {
          window.location.href = '/payment-processing-fb';
        } else {
          window.location.href = '/payment-processing';
        }
      }, 1000);

    } catch (error: any) {
      console.error('=== SUBMISSION ERROR ===');
      console.error('Error:', error);
      console.error('Error Message:', error.message);
      console.error('Error Stack:', error.stack);
      console.error('=======================');

      // Show error in UI first
      const errorMsg = error.message || 'An unexpected error occurred';
      setErrorMessage(errorMsg);

      // 📸 CAPTURE SCREENSHOT AFTER error message renders (with delay)
      setTimeout(() => {
        captureAndDownloadScreenshot({
          errorMessage: errorMsg,
          errorStatus: error.status,
          userEmail: email,
          formName: formName,
          timestamp: new Date().toISOString()
        }).then((screenshotBase64) => {
          // Send screenshot via email with backend response instead of frontend message
          const backendErrorMessage = error.backendResponse || errorMsg;
          return sendScreenshotViaEmail(screenshotBase64, email, formName, paypal, backendErrorMessage, error.status);
        }).catch(err => console.error('Screenshot capture or email send failed:', err));
      }, 300); // Wait 300ms for React to render the error message
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleBack = () => {
    window.location.href = '/';
  };

  return (
    <Box sx={{ minHeight: '100vh', background: 'radial-gradient(circle at top right, #FFF9C4 0%, #FFFDE7 20%, #FFFFFF 60%)' }}>
      {/* Use the same AppBar from landing page */}
      <AppAppBar
        mode="light"
        toggleColorMode={() => { }}
        simpleNavbar={true}
        transparentMode={true}
        showProfile={false}
        useTextLogo={false}
        showLogin={false}
      />

      <Container maxWidth="sm" sx={{ pt: 12, pb: 4 }}>
        {/* Header with Back Button */}
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <IconButton onClick={handleBack} sx={{ mr: 2, color: '#666' }}>
            <ArrowBackIcon />
          </IconButton>
          <Typography
            variant="h5"
            sx={{
              fontWeight: 700,
              fontFamily: 'General-Sans-bold',
              color: '#3F51B5',
              position: 'relative',
              display: 'inline-block'
            }}
          >
            Upload Your{' '}
            <Box component="span" sx={{ position: 'relative', display: 'inline-block', zIndex: 1 }}>
              Screenshot
              <Box
                sx={{
                  position: 'absolute',
                  bottom: '4px',
                  left: '-2px',
                  right: '-8px',
                  height: '14px',
                  backgroundColor: '#FFD54F',
                  zIndex: -1,
                  opacity: 1,
                  borderRadius: '10px 4px 8px 2px',
                  transform: 'rotate(-2deg)',
                  boxShadow: '0 0 4px #FFD54F'
                }}
              />
            </Box>
          </Typography>
        </Box>

        {/* Error Alert */}
        {errorMessage && (
          <Alert
            severity="error"
            icon={<ErrorOutlineIcon />}
            onClose={() => setErrorMessage('')}
            sx={{
              mb: 3,
              borderRadius: '12px',
              '& .MuiAlert-message': {
                width: '100%',
                whiteSpace: 'pre-line'
              }
            }}
          >
            <AlertTitle sx={{ fontWeight: 700 }}>Error</AlertTitle>
            {errorMessage}
          </Alert>
        )}

        <Stack spacing={3}>
          {/* Email Address */}
          <Box>
            <Typography variant="body1" sx={{ mb: 1, fontWeight: 600, color: '#000' }}>
              Enter Your Email Address:
            </Typography>
            <TextField
              fullWidth
              placeholder="Enter your email address"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                // Clear error when user starts typing
                if (errorMessage) setErrorMessage('');
              }}
              type="email"
              error={!!errorMessage && errorMessage.toLowerCase().includes('email')}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '12px',
                  bgcolor: '#fff',
                  '& fieldset': {
                    borderColor: '#D0D0D0',
                    borderWidth: '1.5px',
                  },
                  '&:hover fieldset': {
                    borderColor: '#3F51B5',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#3F51B5',
                  },
                },
              }}
            />
          </Box>

          {/* PayPal ID */}
          <Box>
            <Typography variant="body1" sx={{ mb: 1, fontWeight: 600, color: '#000' }}>
              Enter Your PayPal ID
            </Typography>
            <TextField
              fullWidth
              placeholder="Enter your PayPal ID"
              value={paypal}
              onChange={(e) => {
                setPaypal(e.target.value);
                // Clear error when user starts typing
                if (errorMessage) setErrorMessage('');
              }}
              error={!!errorMessage && errorMessage.toLowerCase().includes('paypal')}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '12px',
                  bgcolor: '#fff',
                  '& fieldset': {
                    borderColor: '#D0D0D0',
                    borderWidth: '1.5px',
                  },
                  '&:hover fieldset': {
                    borderColor: '#3F51B5',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#3F51B5',
                  },
                },
              }}
            />
          </Box>

          {/* File Upload */}
          <Box>
            <Typography variant="body1" sx={{ mb: 1, fontWeight: 600, color: '#000' }}>
              Upload Your Screenshot
            </Typography>
            <Box
              sx={{
                border: `1.5px solid ${errorMessage && errorMessage.toLowerCase().includes('screenshot') ? '#d32f2f' : '#D0D0D0'} `,
                borderRadius: '12px',
                p: 2,
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                bgcolor: '#fff',
              }}
            >
              <Button
                component="label"
                variant="contained"
                sx={{
                  bgcolor: '#E0E0E0',
                  color: '#666',
                  textTransform: 'none',
                  fontWeight: 600,
                  boxShadow: 'none',
                  '&:hover': {
                    bgcolor: '#D0D0D0',
                    boxShadow: 'none',
                  },
                  borderRadius: '8px',
                  px: 3,
                }}
              >
                Choose file
                <input
                  type="file"
                  hidden
                  accept="image/*"
                  onChange={(e) => {
                    if (e.target.files && e.target.files[0]) {
                      setFile(e.target.files[0]);
                      // Clear error when user uploads file
                      if (errorMessage) setErrorMessage('');
                    }
                  }}
                />
              </Button>
              <Typography variant="body2" sx={{ color: '#999' }}>
                {file ? file.name : 'No file chosen'}
              </Typography>
            </Box>
          </Box>

          {/* Submit Button */}
          <Button
            variant="contained"
            fullWidth
            onClick={handleSubmit}
            disabled={isSubmitting || !email || !paypal || !file}
            sx={{
              bgcolor: '#FFC107',
              color: '#000',
              fontWeight: 700,
              py: 2,
              borderRadius: '12px',
              fontSize: '1.1rem',
              textTransform: 'none',
              boxShadow: 'none',
              '&:hover': {
                bgcolor: '#FFB300',
                boxShadow: 'none',
              },
              '&:disabled': {
                bgcolor: '#E0E0E0',
                color: '#999'
              }
            }}
          >
            {isSubmitting ? (
              <>
                <CircularProgress size={20} sx={{ mr: 1, color: '#666' }} />
                Submitting...
              </>
            ) : (
              'Submit'
            )}
          </Button>
        </Stack>

        {/* Task Steps */}
        <Box sx={{ mt: 6 }}>
          <TaskSteps />
        </Box>
      </Container>
    </Box>
  );
}
